import React, { useState, useEffect, useCallback } from 'react';
import { Routes, Route, NavLink, useNavigate, Link } from 'react-router-dom';
import { auth } from '../firebase';
import { apiPost } from '../api';
import { signOut } from 'firebase/auth';
import Dashboard from './Dashboard';
import AllReseller from './AllReseller';
import AllAgency from './AllAgency';
import AllUsers from './AllUsers';
import BlockList from './BlockList';
import GiftUpload from './GiftUpload';
import Game from './Game';
import Store from './Store';
import BannerUpload from './BannerUpload';
import MasterLog from './MasterLog';
import GiftHistory from './GiftHistory';
import './Admin.css';
import '../index.css';
import logo from '../assets/logo.png';
import MasterSalarySheet from './MasterSalarySheet';
import Bill from './Bill';
import TrustFish from './TrustFish';
import LiveStreamsManager from './LiveStreamsManager';
import Greedy from './Greedy';
import RouletteAdmin from './Roulette';
import ReportRequest from './ReportRequest';
import Reels from './Reels';
import AllAdmins from './AllAdmins';
import RewardSettings from './RewardSettings';
import { PERM, useCan } from '../permissions';

// Account creation + admin coin sends now go through the MongoDB backend
// (zoneserver) instead of Firebase Functions. Both endpoints return
// { success, message } — a false success surfaces as an error to the user.
const createUserAsAdmin = (payload) => apiPost('/api/admin/accounts', payload);
const sendCoinAsAdmin = (payload) => apiPost('/api/transaction/send-as-admin', payload);

const Modal = ({ show, onClose, children }) => {
    useEffect(() => {
        const handleEscape = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    return (
        <div className="modal" style={{ display: show ? 'block' : 'none' }}>
            <div className="modal-content">
                <span className="close" onClick={onClose}>&times;</span>
                {children}
            </div>
        </div>
    );
};

const SuccessMessage = ({ message, onClose }) => (
    <div style={{ textAlign: 'center', padding: '12px 0' }}>
        <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'rgba(34,197,94,0.12)',
            border: '2px solid rgba(34,197,94,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px'
        }}>
            <i className="fas fa-check" style={{ fontSize: 24, color: '#22c55e' }} />
        </div>
        <h3 style={{ color: '#22c55e', marginBottom: 8 }}>Success!</h3>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 24 }}>{message}</p>
        <button
            onClick={onClose}
            className="signupbtn"
            style={{ width: '100%', background: '#22c55e' }}
        >
            Continue
        </button>
    </div>
);

const AdminSendCoinForm = ({ onSend, transaction, onClose }) => {
    const [ID, setID] = useState('');
    const [retypeID, setRetypeID] = useState('');
    const [amount, setAmount] = useState('');

    const handleFormSubmit = (e) => {
        e.preventDefault();
        onSend({ ID, retypeID, amount });
    };

    return (
        <>
            <h2>Send Coin</h2>

            {transaction.status === 'sending' && (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <div style={{
                        width: 48, height: 48,
                        border: '3px solid rgba(255,255,255,0.1)',
                        borderLeftColor: '#ff4d6d',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto 12px'
                    }} />
                    <p style={{ color: 'rgba(255,255,255,0.6)' }}>{transaction.message}</p>
                </div>
            )}

            {transaction.status === 'error' && (
                <div style={{
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 8, padding: '12px 14px',
                    marginBottom: 16, color: '#f87171', fontSize: 14
                }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />
                    {transaction.message}
                </div>
            )}

            {transaction.status === 'success' && (
                <SuccessMessage message={transaction.message} onClose={onClose} />
            )}

            {(transaction.status === 'idle' || transaction.status === 'error') && (
                <form onSubmit={handleFormSubmit} className="accinput">
                    <label>Receiver's User ID</label>
                    <input type="text" value={ID} onChange={(e) => setID(e.target.value)} placeholder="Enter user ID" required />
                    <label>Retype Receiver's User ID</label>
                    <input type="text" value={retypeID} onChange={(e) => setRetypeID(e.target.value)} placeholder="Retype user ID" required />
                    <label>Amount</label>
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required min="0.01" step="0.01" />
                    <div className="clearfix">
                        <button type="button" className="cancelbtn" onClick={onClose}>Cancel</button>
                        <button type="submit" className="signupbtn">Send</button>
                    </div>
                </form>
            )}

            {transaction.status === 'sending' && (
                <div className="clearfix">
                    <button type="button" className="cancelbtn" disabled style={{ width: '100%' }}>Sending...</button>
                </div>
            )}
        </>
    );
};

const Admin = ({ userRole }) => {
    // What this account may see. A Super Admin passes every check; an Admin sees
    // only what a Super Admin has switched on for them in Manage Admins.
    const can = useCan();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showResellerModal, setShowResellerModal] = useState(false);
    const [showAgencyModal, setShowAgencyModal] = useState(false);
    const [showSendCoinModal, setShowSendCoinModal] = useState(false);
    const [showAdminModal, setShowAdminModal] = useState(false);

    const [username, setUserName] = useState('');
    const [resellerName, setResellerName] = useState('');
    const [resellerEmail, setResellerEmail] = useState('');
    const [resellerPassword, setResellerPassword] = useState('');
    const [retypeResellerPassword, setRetypeResellerPassword] = useState('');
    const [agencyName, setAgencyName] = useState('');
    const [agencyEmail, setAgencyEmail] = useState('');
    const [agencyPhone, setAgencyPhone] = useState('');
    const [agencyPassword, setAgencyPassword] = useState('');
    const [retypeAgencyPassword, setRetypeAgencyPassword] = useState('');
    const [adminName, setAdminName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [adminPassword, setAdminPassword] = useState('');
    const [retypeAdminPassword, setRetypeAdminPassword] = useState('');
    const [adminRole, setAdminRole] = useState('admin');

    const [transaction, setTransaction] = useState({ status: 'idle', message: '' });

    const navigate = useNavigate();

    const closeSidebar = () => setSidebarOpen(false);

    const handleLogout = useCallback(async () => {
        try {
            await signOut(auth);
            navigate('/');
        } catch (error) {
            console.error('Error logging out:', error);
            alert('Logout failed. Please try again.');
        }
    }, [navigate]);

    const createAccount = async (payload) => {
        setTransaction({ status: 'sending', message: 'Creating account...' });
        try {
            let result = await createUserAsAdmin(payload);

            // The address already has a login — usually someone who uses the
            // app. The server will attach this role to that same account
            // instead of refusing, but only once we say so: an agency or
            // reseller can send coins, so a mistyped address must not hand
            // that power over silently. It tells us whose account it is first.
            if (result.code === 'attach-confirm-required') {
                if (!window.confirm(result.message)) {
                    setTransaction({ status: 'error', message: 'Cancelled — no account was created.' });
                    return;
                }
                result = await createUserAsAdmin({ ...payload, confirmAttach: true });
            }

            if (result.success === false) {
                setTransaction({ status: 'error', message: result.message });
                return;
            }
            setTransaction({ status: 'success', message: result.message });
            setResellerName(''); setResellerEmail(''); setResellerPassword(''); setRetypeResellerPassword('');
            setAgencyName(''); setAgencyEmail(''); setAgencyPassword(''); setRetypeAgencyPassword(''); setUserName(''); setAgencyPhone('');
        } catch (error) {
            setTransaction({ status: 'error', message: `Error: ${error.message}` });
        }
    };

    const createResellerAccount = () => {
        if (!resellerName || !resellerEmail || !resellerPassword || resellerPassword !== retypeResellerPassword) {
            setTransaction({ status: 'error', message: 'Please fill all fields correctly and ensure passwords match.' });
            return;
        }
        createAccount({ email: resellerEmail, password: resellerPassword, name: resellerName, role: 'reseller' });
    };

    const createAgencyAccount = () => {
        if (!agencyName || !agencyEmail || !agencyPassword || agencyPassword !== retypeAgencyPassword) {
            setTransaction({ status: 'error', message: 'Please fill all fields correctly and ensure passwords match.' });
            return;
        }
        createAccount({ email: agencyEmail, password: agencyPassword, name: agencyName, username, phone: agencyPhone, role: 'agency' });
    };

    const createAdminAccount = () => {
        if (!adminName || !adminEmail || !adminPassword || adminPassword !== retypeAdminPassword) {
            setTransaction({ status: 'error', message: 'Please fill all fields correctly and ensure passwords match.' });
            return;
        }
        createAccount({ email: adminEmail, password: adminPassword, name: adminName, role: adminRole });
    };

    const handleSendCoin = async ({ ID, retypeID, amount }) => {
        if (ID !== retypeID) {
            setTransaction({ status: 'error', message: "User IDs don't match." });
            return;
        }
        setTransaction({ status: 'sending', message: 'Processing transaction...' });
        try {
            const result = await sendCoinAsAdmin({ recipientIdStr: ID, amountStr: amount });
            if (result.success === false) {
                setTransaction({ status: 'error', message: result.message });
                return;
            }
            setTransaction({ status: 'success', message: result.message });
        } catch (error) {
            setTransaction({ status: 'error', message: error.message });
        }
    };

    const resetModal = () => setTransaction({ status: 'idle', message: '' });

    const toggleResellerModal = () => { setShowResellerModal(p => !p); resetModal(); };
    const toggleAgencyModal = () => { setShowAgencyModal(p => !p); resetModal(); };
    const toggleSendCoinModal = () => { setShowSendCoinModal(p => !p); resetModal(); };
    const toggleAdminModal = () => {
        setShowAdminModal(p => !p);
        setAdminName(''); setAdminEmail(''); setAdminPassword(''); setRetypeAdminPassword(''); setAdminRole('admin');
        resetModal();
    };

    const navGroups = [
        {
            heading: 'Overview',
            items: [
                { to: '/',              icon: 'fa-tachometer-alt',      label: 'Dashboard' },
            ],
        },
        {
            heading: 'Users',
            items: [
                ...(can(PERM.VIEW_RESELLERS)
                    ? [{ to: '/all-reseller', icon: 'fa-users', label: 'All Reseller' }]
                    : []),
                { to: '/all-agency',    icon: 'fa-building',            label: 'All Agency' },
                { to: '/all-users',     icon: 'fa-user-group',          label: 'All Users' },
                // Manage Admins is superadmin-only by design and has no
                // permission key — it is where send limits and these very
                // switches are set.
                ...(userRole === 'superadmin'
                    ? [
                        { to: '/all-admins', icon: 'fa-shield-alt', label: 'Manage Admins' },
                        { to: '/rewards', icon: 'fa-gift', label: 'Rewards' },
                      ]
                    : []),
            ],
        },
        {
            heading: 'Finance',
            items: [
                { to: '/history-viewer',icon: 'fa-history',             label: 'Master History' },
                { to: '/gift-history',  icon: 'fa-list-alt',            label: 'Gift History' },
                { to: '/mastersalary',  icon: 'fa-file-invoice-dollar', label: 'Master Salary' },
                ...(can(PERM.VIEW_BILL)
                    ? [{ to: '/bill', icon: 'fa-receipt', label: 'Bill' }]
                    : []),
            ],
        },
        // The Content heading itself disappears when none of the three are on,
        // rather than leaving an empty group in the sidebar.
        ...((() => {
            const items = [
                ...(can(PERM.MANAGE_GIFTS)   ? [{ to: '/gift',          icon: 'fa-gift',     label: 'Gift' }]   : []),
                ...(can(PERM.MANAGE_STORE)   ? [{ to: '/store',         icon: 'fa-store',    label: 'Store' }]  : []),
                ...(can(PERM.MANAGE_BANNERS) ? [{ to: '/banner-upload', icon: 'fa-panorama', label: 'Banner' }] : []),
            ];
            return items.length ? [{ heading: 'Content', items }] : [];
        })()),
        {
            heading: 'Safety',
            items: [
                { to: '/block-list',    icon: 'fa-ban',                 label: 'Block List' },
                { to: '/report-request',icon: 'fa-flag',                label: 'Report Request' },
                { to: '/reels',         icon: 'fa-film',                label: 'Reels' },
            ],
        },
        {
            heading: 'Live & Games',
            items: [
                { to: '/live-streams',  icon: 'fa-video',               label: 'Live Streams' },
                ...(can(PERM.MANAGE_GAMES) ? [
                    { to: '/games',     icon: 'fa-gamepad',             label: 'Teen Patti' },
                    { to: '/trustfish', icon: 'fa-fish',                label: 'TrustFish' },
                    { to: '/greedy',    icon: 'fa-dice',                label: 'Greedy' },
                    { to: '/roulette',  icon: 'fa-circle-dot',          label: 'Roulette' },
                ] : []),
            ],
        },
    ];

    return (
        <div className="admin-container">

            {/* ===== HEADER ===== */}
            <header className="header">
                <div className="header-inner">
                    <div className="header-left">
                        <button className="hamburger-btn" onClick={() => setSidebarOpen(p => !p)} aria-label="Menu">
                            <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`} />
                        </button>
                        <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={logo} alt="GolpoStream" style={{
                                height: 44, width: 44, borderRadius: '50%',
                                border: '2px solid rgba(255,77,109,0.4)',
                                objectFit: 'cover'
                            }} />
                        </Link>
                    </div>
                     <div style={{ textAlign: 'center' }}>
                        <span className="header-title">
                            Golpo<span>Stream</span> Admin Panel
                        </span>
                    </div>

                    <div className="header-right">
                        <button className="logout-btn" onClick={handleLogout}>
                            <i className="fa-solid fa-right-from-bracket" />
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* ===== MAIN ===== */}
            <div className="main">

                {/* Mobile overlay */}
                <div
                    className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`}
                    onClick={closeSidebar}
                />

                {/* ===== SIDEBAR ===== */}
                <div className={`leftside ${sidebarOpen ? 'open' : ''}`} style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

                    {/* Admin badge */}
                    <div style={{ padding: '16px 16px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 40, height: 40, borderRadius: 12, background: 'linear-gradient(135deg,#ff4d6d,#c026d3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className="fas fa-shield-alt" style={{ color: '#fff', fontSize: 16 }} />
                            </div>
                            <div>
                                <p style={{ fontWeight: 700, color: '#fff', fontSize: 13, margin: 0, lineHeight: 1.3 }}>Admin Panel</p>
                                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 20, background: 'rgba(255,77,109,0.15)', color: '#ff4d6d', border: '1px solid rgba(255,77,109,0.25)' }}>{userRole === 'superadmin' ? 'Super Admin' : 'Admin'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Nav groups */}
                    <div style={{ padding: '10px', flex: 1, overflowY: 'auto' }}>
                        {navGroups.map(({ heading, items }) => (
                            <div key={heading} style={{ marginBottom: 6 }}>
                                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', padding: '8px 10px 4px', margin: 0 }}>
                                    {heading}
                                </p>
                                {items.map(({ to, icon, label }) => (
                                    <NavLink
                                        key={to}
                                        to={to}
                                        end={to === '/'}
                                        onClick={closeSidebar}
                                        style={({ isActive }) => ({
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            padding: '9px 12px', borderRadius: 10, marginBottom: 2,
                                            textDecoration: 'none', fontSize: 13,
                                            fontWeight: isActive ? 700 : 500,
                                            background: isActive ? 'rgba(255,77,109,0.12)' : 'transparent',
                                            color: isActive ? '#ff4d6d' : 'rgba(255,255,255,0.55)',
                                            borderLeft: `3px solid ${isActive ? '#ff4d6d' : 'transparent'}`,
                                            transition: 'all 0.15s',
                                        })}
                                    >
                                        <i className={`fas ${icon}`} style={{ width: 16, textAlign: 'center', fontSize: 13, flexShrink: 0 }} />
                                        {label}
                                    </NavLink>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* Sign out */}
                    <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
                        <button
                            onClick={handleLogout}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.15)', color: '#f87171', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        >
                            <i className="fas fa-sign-out-alt" style={{ width: 16, textAlign: 'center' }} />
                            Sign Out
                        </button>
                    </div>
                </div>

                {/* ===== RIGHT CONTENT ===== */}
                <div className="right">

                    {/* Action buttons */}
                    <div className="createacc">
                        {can(PERM.CREATE_RESELLER) && (
                            <button
                                onClick={toggleResellerModal}
                                style={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff' }}
                            >
                                <i className="fas fa-user-plus" style={{ marginRight: 6 }} />
                                Create Reseller
                            </button>
                        )}
                        {can(PERM.CREATE_AGENCY) && (
                            <button
                                onClick={toggleAgencyModal}
                                style={{ background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff' }}
                            >
                                <i className="fas fa-building" style={{ marginRight: 6 }} />
                                Create Agency
                            </button>
                        )}
                        <button
                            onClick={toggleSendCoinModal}
                            style={{ background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', color: '#fff' }}
                        >
                            <i className="fas fa-paper-plane" style={{ marginRight: 6 }} />
                            Send Coin
                        </button>
                        {userRole === 'superadmin' && (
                            <button
                                onClick={toggleAdminModal}
                                style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)', color: '#fff' }}
                            >
                                <i className="fas fa-user-shield" style={{ marginRight: 6 }} />
                                Create Admin
                            </button>
                        )}
                    </div>

                    {/* Routes */}
                    <div className="index">
                        <Routes>
                            <Route path="/"               element={<Dashboard />} />
                            {can(PERM.VIEW_RESELLERS) && <Route path="/all-reseller" element={<AllReseller />} />}
                            <Route path="/all-agency"     element={<AllAgency />} />
                            <Route path="/all-users"      element={<AllUsers />} />
                            <Route path="/history-viewer" element={<MasterLog />} />
                            <Route path="/gift-history"   element={<GiftHistory />} />
                            <Route path="/mastersalary"   element={<MasterSalarySheet />} />
                            {can(PERM.VIEW_BILL)      && <Route path="/bill" element={<Bill />} />}
                            {can(PERM.MANAGE_GIFTS)   && <Route path="/gift"          element={<GiftUpload />} />}
                            {can(PERM.MANAGE_STORE)   && <Route path="/store"         element={<Store />} />}
                            {can(PERM.MANAGE_BANNERS) && <Route path="/banner-upload" element={<BannerUpload />} />}
                            <Route path="/block-list"     element={<BlockList />} />
                            <Route path="/live-streams"   element={<LiveStreamsManager />} />
                            {can(PERM.MANAGE_GAMES) && <Route path="/games"     element={<Game />} />}
                            {can(PERM.MANAGE_GAMES) && <Route path="/trustfish" element={<TrustFish />} />}
                            {can(PERM.MANAGE_GAMES) && <Route path="/greedy"    element={<Greedy />} />}
                            {can(PERM.MANAGE_GAMES) && <Route path="/roulette"  element={<RouletteAdmin />} />}
                            <Route path="/report-request" element={<ReportRequest />} />
                            <Route path="/reels" element={<Reels />} />
                            {userRole === 'superadmin' && <Route path="/all-admins" element={<AllAdmins />} />}
                            {userRole === 'superadmin' && <Route path="/rewards" element={<RewardSettings />} />}
                        </Routes>
                    </div>
                </div>
            </div>

            {/* ===== MODALS ===== */}

            {/* Create Reseller */}
            <Modal show={showResellerModal} onClose={toggleResellerModal}>
                {transaction.status === 'success' ? (
                    <SuccessMessage message={transaction.message} onClose={toggleResellerModal} />
                ) : (
                    <>
                        <h2>Create a Reseller</h2>
                        {transaction.status === 'error' && (
                            <div style={{
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: 8, padding: '12px 14px', marginBottom: 16, color: '#f87171', fontSize: 14
                            }}>
                                {transaction.message}
                            </div>
                        )}
                        {transaction.status === 'sending' && (
                            <div style={{
                                background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
                                borderRadius: 8, padding: '12px 14px', marginBottom: 16, color: '#93c5fd', fontSize: 14
                            }}>
                                {transaction.message}
                            </div>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); createResellerAccount(); }} className="accinput">
                            <label>Reseller Name</label>
                            {/* 16 is the app's limit too — the name shows on a
                                narrow strip beside the photo. Capped here so it
                                is obvious while typing, and again on the server,
                                which is what actually enforces it. */}
                            <input type="text" maxLength={16} value={resellerName} onChange={(e) => setResellerName(e.target.value)} placeholder="Full name (max 16)" />
                            <label>Email</label>
                            <input type="email" value={resellerEmail} onChange={(e) => setResellerEmail(e.target.value)} placeholder="email@example.com" />
                            <label>Password</label>
                            <input type="password" value={resellerPassword} onChange={(e) => setResellerPassword(e.target.value)} placeholder="Password" />
                            <label>Retype Password</label>
                            <input type="password" value={retypeResellerPassword} onChange={(e) => setRetypeResellerPassword(e.target.value)} placeholder="Retype password" />
                            <div className="clearfix">
                                <button type="button" className="cancelbtn" onClick={toggleResellerModal} disabled={transaction.status === 'sending'}>Cancel</button>
                                <button type="submit" className="signupbtn" disabled={transaction.status === 'sending'}>
                                    {transaction.status === 'sending' ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </Modal>

            {/* Create Agency */}
            <Modal show={showAgencyModal} onClose={toggleAgencyModal}>
                {transaction.status === 'success' ? (
                    <SuccessMessage message={transaction.message} onClose={toggleAgencyModal} />
                ) : (
                    <>
                        <h2>Create an Agency</h2>
                        {transaction.status === 'error' && (
                            <div style={{
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: 8, padding: '12px 14px', marginBottom: 16, color: '#f87171', fontSize: 14
                            }}>
                                {transaction.message}
                            </div>
                        )}
                        {transaction.status === 'sending' && (
                            <div style={{
                                background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
                                borderRadius: 8, padding: '12px 14px', marginBottom: 16, color: '#93c5fd', fontSize: 14
                            }}>
                                {transaction.message}
                            </div>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); createAgencyAccount(); }} className="accinput">
                            <label>Username</label>
                            <input type="text" value={username} onChange={(e) => setUserName(e.target.value)} placeholder="Username" />
                            <label>Agency Name</label>
                            <input type="text" maxLength={16} value={agencyName} onChange={(e) => setAgencyName(e.target.value)} placeholder="Agency name (max 16)" />
                            <label>Email</label>
                            <input type="email" value={agencyEmail} onChange={(e) => setAgencyEmail(e.target.value)} placeholder="email@example.com" />
                            <label>Phone Number</label>
                            <input type="tel" value={agencyPhone} onChange={(e) => setAgencyPhone(e.target.value)} placeholder="+880..." />
                            <label>Password</label>
                            <input type="password" value={agencyPassword} onChange={(e) => setAgencyPassword(e.target.value)} placeholder="Password" />
                            <label>Retype Password</label>
                            <input type="password" value={retypeAgencyPassword} onChange={(e) => setRetypeAgencyPassword(e.target.value)} placeholder="Retype password" />
                            <div className="clearfix">
                                <button type="button" className="cancelbtn" onClick={toggleAgencyModal} disabled={transaction.status === 'sending'}>Cancel</button>
                                <button type="submit" className="signupbtn" disabled={transaction.status === 'sending'}>
                                    {transaction.status === 'sending' ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </Modal>

            {/* Send Coin */}
            <Modal show={showSendCoinModal} onClose={toggleSendCoinModal}>
                <AdminSendCoinForm
                    onSend={handleSendCoin}
                    transaction={transaction}
                    onClose={toggleSendCoinModal}
                />
            </Modal>

            {/* Create Admin (superadmin only) */}
            <Modal show={showAdminModal} onClose={toggleAdminModal}>
                {transaction.status === 'success' ? (
                    <SuccessMessage message={transaction.message} onClose={toggleAdminModal} />
                ) : (
                    <>
                        <h2>Create an Admin</h2>
                        {transaction.status === 'error' && (
                            <div style={{
                                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: 8, padding: '12px 14px', marginBottom: 16, color: '#f87171', fontSize: 14
                            }}>
                                {transaction.message}
                            </div>
                        )}
                        {transaction.status === 'sending' && (
                            <div style={{
                                background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)',
                                borderRadius: 8, padding: '12px 14px', marginBottom: 16, color: '#d8b4fe', fontSize: 14
                            }}>
                                {transaction.message}
                            </div>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); createAdminAccount(); }} className="accinput">
                            <label>Full Name</label>
                            <input type="text" value={adminName} onChange={(e) => setAdminName(e.target.value)} placeholder="Admin full name" />
                            <label>Email</label>
                            <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} placeholder="email@example.com" />
                            <label>Role</label>
                            <select
                                value={adminRole}
                                onChange={(e) => setAdminRole(e.target.value)}
                                style={{
                                    width: '100%', padding: '10px 12px', borderRadius: 8, marginBottom: 14,
                                    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
                                    color: '#fff', fontSize: 14, outline: 'none'
                                }}
                            >
                                <option value="admin" style={{ background: '#1a1a2e' }}>Admin</option>
                                <option value="superadmin" style={{ background: '#1a1a2e' }}>Super Admin</option>
                            </select>
                            <label>Password</label>
                            <input type="password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} placeholder="Password" />
                            <label>Retype Password</label>
                            <input type="password" value={retypeAdminPassword} onChange={(e) => setRetypeAdminPassword(e.target.value)} placeholder="Retype password" />
                            <div className="clearfix">
                                <button type="button" className="cancelbtn" onClick={toggleAdminModal} disabled={transaction.status === 'sending'}>Cancel</button>
                                <button
                                    type="submit"
                                    className="signupbtn"
                                    style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}
                                    disabled={transaction.status === 'sending'}
                                >
                                    {transaction.status === 'sending' ? 'Creating...' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default Admin;
